import { APP_DEFAULTS, APP_MODES, type AppMode } from '@psspl/config';

export interface WebEnv {
  readonly appName: string;
  readonly companyName: string;
  readonly mode: AppMode;
  readonly isProduction: boolean;
  readonly apiBaseUrl: string;
  /**
   * Public Microsoft Entra settings for "Continue with Microsoft" (ADR-29). Null
   * when `VITE_MICROSOFT_CLIENT_ID` is not set, so the sign-in screen can say that
   * sign-in is not configured instead of sending the user to a broken Microsoft page.
   */
  readonly microsoft: MicrosoftSettings | null;
}

export interface MicrosoftSettings {
  readonly clientId: string;
  readonly tenant: string;
}

/**
 * Web-only env keys.
 *
 * `ENV_KEYS.WEB` in `@psspl/config` is owned by the backend/architecture agent, so
 * this name is declared here rather than edited into the shared package.
 * See cross-package dependency **DEP-8**: it should move to `ENV_KEYS.WEB`.
 */
const WEB_ENV_KEYS = {
  COMPANY_NAME: 'VITE_COMPANY_NAME',
} as const;

function readMode(): AppMode {
  const raw = import.meta.env.VITE_APP_ENV;

  if (raw !== undefined && APP_MODES.includes(raw)) {
    return raw;
  }

  return import.meta.env.PROD ? 'production' : 'development';
}

const isProduction = import.meta.env.PROD;

/**
 * Reads a Vite env value as a string.
 *
 * `import.meta.env[key]` is typed `any`, so every read is funnelled through here and
 * narrowed to `string | undefined`. A non-string value is treated as absent rather
 * than coerced, which keeps a malformed `.env` from reaching a component.
 */
function readViteEnv(key: string): string | undefined {
  const value: unknown = (import.meta.env as unknown as Record<string, unknown>)[key];

  return typeof value === 'string' ? value : undefined;
}

/**
 * `MICROSOFT_TENANT=common` is the documented deployment value (ADR-28); the API
 * pins the accepted tenants, so the browser-side authority is not a trust boundary.
 */
const DEFAULT_MICROSOFT_TENANT = 'common';

function readMicrosoftSettings(): MicrosoftSettings | null {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID?.trim() ?? '';
  const tenant = import.meta.env.VITE_MICROSOFT_TENANT?.trim() ?? '';

  if (clientId === '') {
    return null;
  }

  return { clientId, tenant: tenant === '' ? DEFAULT_MICROSOFT_TENANT : tenant };
}

export const webEnv: WebEnv = {
  appName: import.meta.env.VITE_APP_NAME ?? APP_DEFAULTS.appName,
  companyName: readViteEnv(WEB_ENV_KEYS.COMPANY_NAME) ?? APP_DEFAULTS.organisation,
  mode: readMode(),
  isProduction,
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '/api',
  microsoft: readMicrosoftSettings(),
};
